import { addMilliseconds, differenceInCalendarDays, isBefore, subDays, subMilliseconds } from 'date-fns'
import { compact, flatten, isEmpty, remove, sumBy, values } from 'lodash-es'

import { isSupportedCountry } from '@forto-dev/phoenix-ui-core'
import { HorizontalTimelineType } from '@forto-dev/phoenix-ui-core/ship'
import { LCLMainRoute, LineItem, QuoteOption, RouteQuote, Sheet } from '@freighthub/freighthub-quotation-sdk'

import { isAddress, isAirPort, isPort, isSeaPort } from '@/features/trackTrace'
import { useLocalisation } from '@/utils/localisation'

import { isCountryEU } from './euCountryCodes'

export const getLineItemsSortedByType = (option: QuoteOption): LineItem[] => {
  const {
    originCustomsQuote,
    destinationCustomsQuote,
    insuranceQuote,
    handlingFeeLineItem,
    voucherQuote,
    carbonOffsetQuote,
    routeQuotes: { preQuote, mainQuote, postQuote },
    vgmQuote,
  } = option

  const mainQuoteLineItems = [...mainQuote.lineItems]
  const handlingLineItems = remove(mainQuoteLineItems, (lineItem) => lineItem.type === 'handling')

  // For FCL the HandlingLineItem is included in the Freight
  // For ManualQuotes we create a HandlingLineItem additionally
  if (
    option.routeQuotes.mainQuote.source === 'MAERSK_SPOT' ||
    (option.type === 'lcl' && option.isManual !== true && option.handlingFeeLineItem !== null)
  ) {
    if (handlingFeeLineItem != null) {
      handlingLineItems.push(handlingFeeLineItem)
    }
  }

  const lineItems = [
    preQuote?.lineItems,
    vgmQuote?.lineItems,
    mainQuoteLineItems,
    postQuote?.lineItems,
    originCustomsQuote?.lineItems,
    destinationCustomsQuote?.lineItems,
    insuranceQuote?.lineItems,
    carbonOffsetQuote?.lineItems,
    voucherQuote?.lineItems,
    handlingLineItems,
  ]

  return compact(flatten(lineItems))
}

export const getTotalPriceInEUR = (quote: QuoteOption): number => {
  return sumBy(getLineItemsSortedByType(quote), (line) => line.totalEUR || 0)
}

export const getCarrier = (quote: QuoteOption): string => {
  return quote.routeQuotes.mainQuote.route.carrier_id || '--'
}

export const getAirline = (quote: QuoteOption): string => {
  return (quote.routeQuotes.mainQuote.route as LCLMainRoute).airline || '--'
}

export const getOriginAddressFromQuoteSheet = (quoteSheet?: Sheet): string | undefined => {
  const origin = quoteSheet?.request.journey.origin

  if (origin && isAddress(origin)) {
    return origin.city || origin.country
  }
}

export const getBookingLink = (quoteSheet: Sheet) => {
  const quoteSheetID = quoteSheet._id
  const quoteSheetIDEncoded = encodeURIComponent(quoteSheetID.toString())

  return `${window.location.protocol}//${window.location.host}/quotes/${quoteSheetIDEncoded}/book?quoteSheetID=${quoteSheetIDEncoded}`
}

export const getDestinationAddressFromQuoteSheet = (quoteSheet?: Sheet): string | undefined => {
  const destination = quoteSheet?.request.journey.destination

  if (destination && isAddress(destination)) {
    return destination.city || destination.country
  }
}
export const isExportFromEU = (quoteSheet: Sheet): boolean => {
  return isCountryEU(quoteSheet.request.journey.origin.countryCode)
}

export const shouldIgnorePreCarriageDuration = (quoteSheet: Sheet): boolean => {
  return !!(
    isExportFromEU(quoteSheet) &&
    quoteSheet.request.type === 'fcl' &&
    quoteSheet.request.modes.indexOf('sea') >= 0
  )
}

export const getETD = (quote: QuoteOption, ignorePreCarriageDuration: boolean): Date | null => {
  const { etd } = quote.routeQuotes.mainQuote.route
  if (etd == null || isEmpty(etd?.toString())) return null

  if (quote.routeQuotes.preQuote != null && !ignorePreCarriageDuration) {
    const DEFAULT_PRE_CARRIAGE_DURATION = 4 * 24 * 60 * 60 * 1000
    const preCarriageDuration = quote.routeQuotes.preQuote.route.duration || DEFAULT_PRE_CARRIAGE_DURATION
    return subMilliseconds(new Date(etd), preCarriageDuration)
  }

  return new Date(etd)
}

export const getCyCutoff = (quote: QuoteOption): Date | null => {
  return quote.routeQuotes.mainQuote.route.routeSchedule?.cutOff
    ? new Date(quote.routeQuotes.mainQuote.route.routeSchedule.cutOff)
    : null
}

export const getDuration = (quote: QuoteOption, ignorePreCarriageDuration: boolean): number => {
  const routeQuotesForDurationCalculation: RouteQuote[] = ignorePreCarriageDuration
    ? [quote.routeQuotes.mainQuote, quote.routeQuotes.postQuote].filter((quote) => quote != null)
    : values<RouteQuote>(quote.routeQuotes)
  // total duration in ms
  return sumBy(compact(routeQuotesForDurationCalculation), (quote) => quote.route.duration || 0)
}

export const getDurationInDays = (durationInMs: number): number => {
  return Math.ceil(durationInMs / (1000 * 60 * 60 * 24))
}

export const getDaysOfDeparture = (quote: QuoteOption): ('mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun')[] => {
  return quote.routeQuotes.mainQuote.route.daysOfDeparture || []
}

export const getCutOff = (quote: QuoteOption): number | undefined => {
  return quote.routeQuotes.mainQuote.route.cutOff
}

export const optionSubjectToAvailability = (option: QuoteOption): boolean => {
  const minCutOff = 10
  const minETDBook = 12

  const isD2X = option.routeQuotes.preQuote != null
  if (isD2X) {
    const etd = getETD(option, true) ?? new Date()
    const diff = differenceInCalendarDays(etd, new Date())
    return diff >= 6
  }
  if (!option.routeQuotes.mainQuote.route.routeSchedule) {
    return true
  }

  const today = new Date()
  const { cutOff } = option.routeQuotes.mainQuote.route.routeSchedule
  let cutOffDate
  if (cutOff) {
    cutOffDate = subDays(cutOff, minCutOff)
  } else {
    cutOffDate = subDays(option.routeQuotes.mainQuote.route.routeSchedule.etd, minETDBook)
  }
  return isBefore(today, cutOffDate)
}

const isOptionFromMaerskSpot = (option: QuoteOption): boolean => {
  return option.routeQuotes.mainQuote.source === 'MAERSK_SPOT'
}

export const getHasEtdDelay = (quote: QuoteOption): boolean => {
  const isMaersk = isOptionFromMaerskSpot(quote)
  const isSubjectToAvailability = !optionSubjectToAvailability(quote)
  return isSubjectToAvailability && !isMaersk
}

export const getETA = (quote: QuoteOption, ignorePreCarriageDuration: boolean): Date | null => {
  const etd = getETD(quote, ignorePreCarriageDuration)
  if (etd == null) return null

  const totalDuration = getDuration(quote, ignorePreCarriageDuration)
  return addMilliseconds(etd, totalDuration)
}

type GetTimelineDataForQuoteArgs = {
  quoteSheet: Sheet
  quoteItem: QuoteOption
  translate: ReturnType<typeof useLocalisation>['translate']
  translateDate: ReturnType<typeof useLocalisation>['translateDate']
}
export const getTimelineDataForQuote = ({
  quoteSheet,
  translateDate,
  quoteItem,
  translate,
}: GetTimelineDataForQuoteArgs): HorizontalTimelineType => {
  const textMap = {
    estimatedCRDLabel: translate('timeline.cargoReady_estimated'),
    estimatedDepartureLabel: translate('timeline.departure_estimated'),
    estimatedArrivalLabel: translate('timeline.arrival_estimated'),
    estimatedDeliveryLabel: translate('timeline.delivery_estimated'),
  }
  const transportType = quoteSheet.request.type
  const isLCL = transportType === 'lcl'
  const sailing = quoteItem.routeQuotes.mainQuote.route.routeSchedule
  const oceanLegs = sailing?.oceanLegs ?? []
  const crd = quoteSheet.request.cargoReadyAt
  const ignorePreCarriageDuration = shouldIgnorePreCarriageDuration(quoteSheet)
  const eta = getETA(quoteItem, ignorePreCarriageDuration)
  const etdForOriginPort = getETD(quoteItem, ignorePreCarriageDuration)
  const pickupAddress = getOriginAddressFromQuoteSheet(quoteSheet)
  const deliveryAddress = getDestinationAddressFromQuoteSheet(quoteSheet)
  const etaForDestinationPort = oceanLegs[oceanLegs.length - 1]?.eta
  const originPort = isLCL ? polFromQuoteForLCL(quoteItem) : sailing?.pol?.name
  const destinationPort = isLCL ? podFromQuoteForLCL(quoteItem) : sailing?.pod?.name

  const originCountryCode = quoteSheet?.request.journey.origin?.countryCode?.toUpperCase()
  const destinationCountryCode = quoteSheet?.request.journey.destination?.countryCode?.toUpperCase()

  return {
    origin: {
      title: pickupAddress ?? '--',
      label: textMap.estimatedCRDLabel,
      labelValue: crd ? translateDate('cargo_ready_date', crd) : '',
      state: 'upcoming',
      progress: 0,
      flag: isSupportedCountry(originCountryCode) ? originCountryCode : undefined,
    },
    departure: {
      title: originPort ?? '--',
      label: textMap.estimatedDepartureLabel,
      labelValue: etdForOriginPort ? translateDate('cargo_pickup_date', etdForOriginPort) : '',
      state: 'upcoming',
      progress: 0,
      transhipmentData:
        oceanLegs.length > 1
          ? {
              total: oceanLegs.length,
              completed: 0,
            }
          : undefined,
    },
    arrival: {
      title: destinationPort ?? '--',
      label: textMap.estimatedArrivalLabel,
      labelValue: etaForDestinationPort ? translateDate('cargo_arrival_date', etaForDestinationPort) : '',
      state: 'upcoming',
      progress: 0,
    },
    delivery: {
      title: deliveryAddress ?? '--',
      label: textMap.estimatedDeliveryLabel,
      labelValue: eta ? translateDate('cargo_delivery_date', eta) : '',
      state: 'upcoming',
      progress: 0,
      flag: isSupportedCountry(destinationCountryCode) ? destinationCountryCode : undefined,
    },
  }
}

export const polFromQuoteForLCL = (quote: QuoteOption): string | null => {
  const { origin } = quote.routeQuotes.mainQuote.route.journey

  return isPort(origin) ? origin.name : null
}

const isOptionWithPOD = (quote: QuoteOption) => {
  const { destination } = quote.routeQuotes.mainQuote.route.journey
  return isSeaPort(destination) || isAirPort(destination)
}

export const podFromQuoteForLCL = (quote: QuoteOption): string | null => {
  const { destination } = quote.routeQuotes.mainQuote.route.journey
  return isOptionWithPOD(quote) && isPort(destination) ? destination.name : null
}

export const isZeroSurchargeLineItem = (lineItem: LineItem): boolean => {
  return lineItem.type === 'surcharge' && lineItem.totalEUR === 0
}
